import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import Dropdown from "../common/Dropdown";
import styles from "./TextWithVoiceSelection.module.css";

interface TextSegment {
    id: string;
    startIndex: number;
    endIndex: number;
    voiceId: string;
    text: string;
}

interface TextWithVoiceSelectionProps {
    fullText: string;
    onTextChange: (text: string) => void;
    segments: TextSegment[];
    onSegmentsChange: (segments: TextSegment[]) => void;
    voiceOptions: any[];
    placeholder?: string;
    onSelectionChange?: (hasSelection: boolean) => void;
    assignVoiceToSelection?: (handler: (voiceId: string) => void) => void;
    autoFillWithLastVoice?: boolean; // Enable auto-fill new text with last selected voice
    onGlobalVoiceApply?: (voiceId: string) => void; // Callback when voice is applied globally
    lastAppliedVoice?: string | null; // Last voice applied globally (from parent)
}

// Deterministic short hash for class names
const hashId = (s: string): string => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    // base36 keeps it short, always positive
    return Math.abs(h).toString(36);
};

// Light pastel palette for text highlights (sequential assignment)
const VOICE_COLORS = [
    "oklch(0.96 0.03 260)", // Soft violet
    "oklch(0.955 0.035 150)", // Soft green
    "oklch(0.96 0.04 70)", // Soft amber
    "oklch(0.96 0.03 25)", // Soft coral
    "oklch(0.96 0.03 200)", // Soft teal
    "oklch(0.96 0.035 320)", // Soft magenta
    "oklch(0.96 0.03 100)", // Soft lime
    "oklch(0.96 0.03 40)", // Soft orange
];

// Saturated avatar colors for speaker pills (readable white initials)
const VOICE_AVATAR_COLORS = [
    "oklch(0.55 0.11 260)", // Violet
    "oklch(0.55 0.12 150)", // Green
    "oklch(0.58 0.12 70)", // Amber
    "oklch(0.58 0.14 25)", // Coral
    "oklch(0.52 0.1 200)", // Teal
    "oklch(0.55 0.14 320)", // Magenta
    "oklch(0.52 0.1 100)", // Olive
    "oklch(0.56 0.13 40)", // Orange
];

// Sequential color assignment based on voice appearance order
const colorFromVoiceIndex = (index: number): string => {
    return VOICE_COLORS[index % VOICE_COLORS.length];
};

const avatarColorFromVoiceIndex = (index: number): string => {
    return VOICE_AVATAR_COLORS[index % VOICE_AVATAR_COLORS.length];
};

// Memoized map: voiceId -> className (with sequential color assignment)
const useVoiceClasses = (voiceIds: string[]) => {
    return useMemo(() => {
        const defs = voiceIds.map((id, index) => {
            const cls = `v-${hashId(id)}`;
            const bg = colorFromVoiceIndex(index);
            const avatar = avatarColorFromVoiceIndex(index);
            return { id, cls, bg, avatar };
        });
        return defs;
    }, [voiceIds]);
};

// Stylesheet component to output CSS once per voice
const VoiceStyles: React.FC<{ defs: { id: string; cls: string; bg: string; avatar: string }[] }> = ({ defs }) => {
    // Build one CSS string; no inline styles on spans anymore
    // Classes are deterministic based on voiceId hash, so same voices get same classes across instances
    // Use !important to ensure styles override any conflicting CSS
    const css = defs
        .map((d) => `.${d.cls}{background:${d.bg} !important;text-decoration:underline !important;}`)
        .join("\n");
    return <style>{css}</style>;
};

// Merge segments using indices only (no text concatenation)
// Only merges adjacent segments with the same voiceId
const mergeSegments = (fullText: string, segs: TextSegment[]): TextSegment[] => {
    if (!segs.length) return [];

    // Already sorted and normalized by normalizeSegments, so just merge adjacents
    const sorted = [...segs].sort((a, b) => {
        if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
        return a.endIndex - b.endIndex;
    });

    const merged: TextSegment[] = [];
    let cur = { ...sorted[0] };

    for (let i = 1; i < sorted.length; i++) {
        const nxt = sorted[i];
        // Only merge if same voice AND adjacent or overlapping
        if (cur.voiceId === nxt.voiceId && nxt.startIndex <= cur.endIndex) {
            // Same voice and adjacent/overlapping -> merge
            cur.endIndex = Math.max(cur.endIndex, nxt.endIndex);
        } else {
            // Different voice or not adjacent -> keep separate
            merged.push(cur);
            cur = { ...nxt };
        }
    }
    merged.push(cur);

    // recompute text lazily from indices to keep truth in one place
    return merged.map((s) => ({
        ...s,
        text: fullText.substring(s.startIndex, s.endIndex),
    }));
};

// Normalize segments: clamp, drop empties, resolve overlaps, then merge
const normalizeSegments = (fullText: string, segs: TextSegment[]): TextSegment[] => {
    if (!segs.length) return [];

    // 1) clamp indices to valid range
    const clamped = segs
        .map((s) => ({
            ...s,
            startIndex: Math.max(0, Math.min(fullText.length, s.startIndex)),
            endIndex: Math.max(0, Math.min(fullText.length, s.endIndex)),
        }))
        .filter((s) => s.endIndex > s.startIndex);

    if (!clamped.length) return [];

    // 2) Sort by start index, then by end index (for consistent ordering)
    const sorted = [...clamped].sort((a, b) => {
        if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
        return a.endIndex - b.endIndex;
    });

    // 3) Resolve overlaps: when segments overlap, split the earlier one and keep the later one
    // This ensures "last applied voice wins"
    const resolved: TextSegment[] = [];

    for (const newSeg of sorted) {
        const toAdd: TextSegment[] = [];

        // Check all existing resolved segments for overlaps with newSeg
        for (const existing of resolved) {
            const overlapStart = Math.max(existing.startIndex, newSeg.startIndex);
            const overlapEnd = Math.min(existing.endIndex, newSeg.endIndex);

            if (overlapStart < overlapEnd) {
                // There's an overlap - split the existing segment
                // Left part (before overlap)
                if (existing.startIndex < overlapStart) {
                    toAdd.push({
                        ...existing,
                        id: `seg-${Date.now()}-${Math.random()}`,
                        endIndex: overlapStart,
                        text: fullText.substring(existing.startIndex, overlapStart),
                    });
                }
                // Right part (after overlap) - only if it doesn't extend beyond newSeg
                if (existing.endIndex > overlapEnd) {
                    toAdd.push({
                        ...existing,
                        id: `seg-${Date.now()}-${Math.random()}`,
                        startIndex: overlapEnd,
                        text: fullText.substring(overlapEnd, existing.endIndex),
                    });
                }
                // The overlapping part is dropped (newSeg will cover it)
            } else {
                // No overlap, keep the existing segment
                toAdd.push(existing);
            }
        }

        // Add the new segment (it wins in overlaps)
        toAdd.push(newSeg);
        resolved.length = 0;
        resolved.push(...toAdd);
    }

    // 4) Merge adjacent segments with the same voice
    return mergeSegments(fullText, resolved);
};

// Robust rebase segments after edit using common prefix/suffix
const rebaseSegmentsAfterEdit = (prevText: string, nextText: string, prevSegments: TextSegment[]): TextSegment[] => {
    if (prevText === nextText) return prevSegments;

    // find common prefix
    let p = 0;
    const minLen = Math.min(prevText.length, nextText.length);
    while (p < minLen && prevText.charCodeAt(p) === nextText.charCodeAt(p)) p++;

    // find common suffix (avoid crossing prefix)
    let s = 0;
    while (
        s < prevText.length - p &&
        s < nextText.length - p &&
        prevText.charCodeAt(prevText.length - 1 - s) === nextText.charCodeAt(nextText.length - 1 - s)
    )
        s++;

    const prevEditStart = p;
    const prevEditEnd = prevText.length - s;
    const nextEditEnd = nextText.length - s;

    const removedLen = prevEditEnd - prevEditStart; // length removed from prev
    const addedLen = nextEditEnd - prevEditStart; // length inserted in next
    const delta = addedLen - removedLen;

    // shift/split segments around [prevEditStart, prevEditEnd)
    const rebased: TextSegment[] = [];
    for (const seg of prevSegments) {
        const { startIndex, endIndex } = seg;

        // completely before edit window
        if (endIndex <= prevEditStart) {
            rebased.push(seg);
            continue;
        }
        // completely after edit window
        if (startIndex >= prevEditEnd) {
            rebased.push({ ...seg, startIndex: startIndex + delta, endIndex: endIndex + delta });
            continue;
        }
        // overlap with edit window
        // left remainder
        if (startIndex < prevEditStart) {
            rebased.push({
                ...seg,
                id: `seg-${Date.now()}-${Math.random()}`,
                endIndex: prevEditStart,
                text: prevText.substring(startIndex, prevEditStart),
            });
        }
        // right remainder (shifted)
        if (endIndex > prevEditEnd) {
            rebased.push({
                ...seg,
                id: `seg-${Date.now()}-${Math.random()}`,
                startIndex: prevEditStart + delta,
                endIndex: endIndex + delta,
                text: nextText.substring(prevEditStart + delta, endIndex + delta),
            });
        }
        // middle (the replaced area) is dropped; newly typed text has no voice yet
    }

    return normalizeSegments(nextText, rebased);
};

// Helper function to split overlapping segments instead of deleting them
const applyVoiceToRange = (
    segs: TextSegment[],
    rangeStart: number,
    rangeEnd: number,
    voiceId: string,
    fullText: string,
): TextSegment[] => {
    const next: TextSegment[] = [];

    for (const s of segs) {
        const overlapStart = Math.max(s.startIndex, rangeStart);
        const overlapEnd = Math.min(s.endIndex, rangeEnd);

        if (overlapStart >= overlapEnd) {
            // no overlap
            next.push(s);
            continue;
        }

        // left remainder
        if (s.startIndex < overlapStart) {
            next.push({
                ...s,
                id: `seg-${Date.now()}-${Math.random()}`,
                startIndex: s.startIndex,
                endIndex: overlapStart,
                text: fullText.substring(s.startIndex, overlapStart),
            });
        }

        // right remainder
        if (s.endIndex > overlapEnd) {
            next.push({
                ...s,
                id: `seg-${Date.now()}-${Math.random()}`,
                startIndex: overlapEnd,
                endIndex: s.endIndex,
                text: fullText.substring(overlapEnd, s.endIndex),
            });
        }
    }

    // new "selection" segment
    next.push({
        id: `seg-${Date.now()}-${Math.random()}`,
        startIndex: rangeStart,
        endIndex: rangeEnd,
        voiceId,
        text: fullText.substring(rangeStart, rangeEnd),
    });

    return normalizeSegments(fullText, next);
};

const TextWithVoiceSelection: React.FC<TextWithVoiceSelectionProps> = ({
    fullText,
    onTextChange,
    segments,
    onSegmentsChange,
    voiceOptions,
    placeholder = "Tapez votre texte ici...",
    onSelectionChange,
    assignVoiceToSelection,
    autoFillWithLastVoice = false,
    onGlobalVoiceApply,
    lastAppliedVoice,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [selectedText, setSelectedText] = useState<string>("");
    const [selectionStart, setSelectionStart] = useState<number>(0);
    const [selectionEnd, setSelectionEnd] = useState<number>(0);
    const [editingVoiceId, setEditingVoiceId] = useState<string | null>(null);
    const [lastSelectedVoiceId, setLastSelectedVoiceId] = useState<string>(lastAppliedVoice || "");
    const [textareaHeight, setTextareaHeight] = useState<number>(0);
    const prevFullTextRef = useRef<string>(fullText);
    const hasInitializedVoiceRef = useRef<boolean>(false);
    const autoFillTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSelectingRef = useRef<boolean>(false);
    const badgeContainerRef = useRef<HTMLDivElement>(null);
    const AUTO_FILL_DEBOUNCE_MS = 300;

    // Sort segments by start index - memoized to prevent infinite loops
    const sortedSegments = useMemo(() => {
        return [...segments].sort((a, b) => a.startIndex - b.startIndex);
    }, [segments]);

    // Handle text selection
    const handleTextSelection = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = fullText.substring(start, end);

        isSelectingRef.current = start !== end;

        if (selected.trim().length > 0) {
            setSelectionStart(start);
            setSelectionEnd(end);
            setSelectedText(selected);
            if (onSelectionChange) {
                onSelectionChange(true);
            }
        } else {
            if (onSelectionChange) {
                onSelectionChange(false);
            }
        }
    }, [fullText, onSelectionChange]);

    // Store callbacks and state in refs to avoid recreating handleVoiceSelect
    const onSegmentsChangeRef = useRef(onSegmentsChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const selectedTextRef = useRef(selectedText);
    const selectionStartRef = useRef(selectionStart);
    const selectionEndRef = useRef(selectionEnd);
    const segmentsRef = useRef(segments);
    const fullTextRef = useRef(fullText);

    useEffect(() => {
        onSegmentsChangeRef.current = onSegmentsChange;
    }, [onSegmentsChange]);

    useEffect(() => {
        onSelectionChangeRef.current = onSelectionChange;
    }, [onSelectionChange]);

    useEffect(() => {
        selectedTextRef.current = selectedText;
    }, [selectedText]);

    useEffect(() => {
        selectionStartRef.current = selectionStart;
    }, [selectionStart]);

    useEffect(() => {
        selectionEndRef.current = selectionEnd;
    }, [selectionEnd]);

    useEffect(() => {
        segmentsRef.current = segments;
    }, [segments]);

    useEffect(() => {
        fullTextRef.current = fullText;
    }, [fullText]);

    // Store onGlobalVoiceApply in ref
    const onGlobalVoiceApplyRef = useRef(onGlobalVoiceApply);
    useEffect(() => {
        onGlobalVoiceApplyRef.current = onGlobalVoiceApply;
    }, [onGlobalVoiceApply]);

    // Handle voice selection for selected text - exposed for external use
    // This function is stable and uses refs to access current values
    const handleVoiceSelect = useCallback(
        (voiceId: string) => {
            const currentSelectionStart = selectionStartRef.current;
            const currentSelectionEnd = selectionEndRef.current;
            const currentSegments = segmentsRef.current;

            // Track last selected voice for auto-fill (always do this, even without selection)
            setLastSelectedVoiceId(voiceId);
            // Mark as initialized since user explicitly selected a voice
            hasInitializedVoiceRef.current = true;

            // If no selection, trigger global voice apply for ALL capsules
            if (currentSelectionStart === currentSelectionEnd) {
                if (onGlobalVoiceApplyRef.current) {
                    onGlobalVoiceApplyRef.current(voiceId);
                }
                if (onSelectionChangeRef.current) {
                    onSelectionChangeRef.current(false);
                }
                return;
            }

            // Split overlapping segments instead of deleting them
            const updated = applyVoiceToRange(
                currentSegments,
                currentSelectionStart,
                currentSelectionEnd,
                voiceId,
                fullTextRef.current,
            );

            if (onSegmentsChangeRef.current) {
                onSegmentsChangeRef.current(updated);
            }

            // Clear selection
            setSelectedText("");
            if (textareaRef.current) {
                textareaRef.current.setSelectionRange(currentSelectionEnd, currentSelectionEnd);
            }
            if (onSelectionChangeRef.current) {
                onSelectionChangeRef.current(false);
            }
        },
        [], // No dependencies - all values accessed via refs
    );

    // Expose handler for external use
    const assignVoiceToSelectionRef = useRef(assignVoiceToSelection);
    const handleVoiceSelectRef = useRef(handleVoiceSelect);

    useEffect(() => {
        assignVoiceToSelectionRef.current = assignVoiceToSelection;
    }, [assignVoiceToSelection]);

    useEffect(() => {
        handleVoiceSelectRef.current = handleVoiceSelect;
    }, [handleVoiceSelect]);

    useEffect(() => {
        if (assignVoiceToSelectionRef.current && handleVoiceSelectRef.current) {
            assignVoiceToSelectionRef.current(handleVoiceSelectRef.current);
        }
    }, [assignVoiceToSelection, handleVoiceSelect]);

    // Keep the default voice in sync with the global lastAppliedVoice
    useEffect(() => {
        if (!lastAppliedVoice) return;

        // Update local default immediately
        setLastSelectedVoiceId(lastAppliedVoice);

        // Debounced auto-fill for unassigned ranges
        if (autoFillTimerRef.current) clearTimeout(autoFillTimerRef.current);
        autoFillTimerRef.current = setTimeout(() => {
            if (!autoFillWithLastVoice) return;
            if (isSelectingRef.current) return; // skip while user is selecting

            // Only fill gaps (don't touch existing voiced segments)
            const sortedSegs = [...segments].sort((a, b) => a.startIndex - b.startIndex);
            const gaps: Array<{ start: number; end: number }> = [];
            let cursor = 0;

            sortedSegs.forEach((s) => {
                if (s.startIndex > cursor) gaps.push({ start: cursor, end: s.startIndex });
                cursor = Math.max(cursor, s.endIndex);
            });
            if (cursor < fullText.length) gaps.push({ start: cursor, end: fullText.length });

            if (gaps.length === 0) return;

            let next = [...segments];
            gaps.forEach(({ start, end }) => {
                const chunk = fullText.substring(start, end);
                if (chunk.trim().length === 0) return; // ignore whitespace
                next.push({
                    id: `seg-${Date.now()}-${Math.random()}`,
                    startIndex: start,
                    endIndex: end,
                    voiceId: lastAppliedVoice,
                    text: chunk,
                });
            });
            onSegmentsChange(normalizeSegments(fullText, next));
        }, AUTO_FILL_DEBOUNCE_MS);

        return () => {
            if (autoFillTimerRef.current) {
                clearTimeout(autoFillTimerRef.current);
                autoFillTimerRef.current = null;
            }
        };
    }, [lastAppliedVoice, fullText, segments, autoFillWithLastVoice, onSegmentsChange]);

    // Debounced auto-fill while typing
    useEffect(() => {
        if (!autoFillWithLastVoice || !lastSelectedVoiceId) {
            prevFullTextRef.current = fullText;
            return;
        }

        // If there's text but no segments, auto-assign the voice to ALL text
        if (fullText && fullText.trim().length > 0 && segments.length === 0) {
            const newSegment: TextSegment = {
                id: `segment-${Date.now()}-${Math.random()}`,
                startIndex: 0,
                endIndex: fullText.length,
                voiceId: lastSelectedVoiceId,
                text: fullText,
            };
            onSegmentsChange(normalizeSegments(fullText, [newSegment]));
            prevFullTextRef.current = fullText;
            return;
        }

        // Don't rerun if text length didn't actually change
        if (prevFullTextRef.current === fullText) {
            return;
        }

        prevFullTextRef.current = fullText;

        // Debounce the auto-fill
        if (autoFillTimerRef.current) clearTimeout(autoFillTimerRef.current);
        autoFillTimerRef.current = setTimeout(() => {
            if (isSelectingRef.current) return; // skip while user is selecting

            // Find gaps and fill with lastSelectedVoiceId
            const sortedSegs = [...segments].sort((a, b) => a.startIndex - b.startIndex);
            const gaps: Array<{ start: number; end: number }> = [];
            let cursor = 0;

            sortedSegs.forEach((s) => {
                if (s.startIndex > cursor) gaps.push({ start: cursor, end: s.startIndex });
                cursor = Math.max(cursor, s.endIndex);
            });
            if (cursor < fullText.length) gaps.push({ start: cursor, end: fullText.length });

            if (gaps.length === 0) return;

            let next = [...segments];
            gaps.forEach(({ start, end }) => {
                const chunk = fullText.substring(start, end);
                if (chunk.trim().length === 0) return;
                next.push({
                    id: `seg-${Date.now()}-${Math.random()}`,
                    startIndex: start,
                    endIndex: end,
                    voiceId: lastSelectedVoiceId,
                    text: chunk,
                });
            });

            onSegmentsChange(normalizeSegments(fullText, next));
        }, AUTO_FILL_DEBOUNCE_MS);

        return () => {
            if (autoFillTimerRef.current) {
                clearTimeout(autoFillTimerRef.current);
                autoFillTimerRef.current = null;
            }
        };
    }, [fullText, segments, lastSelectedVoiceId, autoFillWithLastVoice, onSegmentsChange]);

    // Initialize last selected voice from existing segments if no lastAppliedVoice is provided
    // This handles the case where a capsule has existing segments but no global default yet
    useEffect(() => {
        // Only initialize if we don't have a lastAppliedVoice from parent and no local default
        if (!lastAppliedVoice && !lastSelectedVoiceId && segments.length > 0) {
            // Find the most recent segment (highest startIndex)
            const sortedSegs = [...segments].sort((a, b) => b.startIndex - a.startIndex);
            if (sortedSegs[0]?.voiceId) {
                setLastSelectedVoiceId(sortedSegs[0].voiceId);
            }
        }
    }, [segments, lastSelectedVoiceId, lastAppliedVoice]);

    // Get unique voice IDs for CSS class generation
    const uniqueVoiceIds = useMemo(() => {
        const set = new Set<string>();
        segments.forEach((s) => s.voiceId && set.add(s.voiceId));
        return Array.from(set);
    }, [segments]);

    // Generate stable CSS classes for each voice
    const voiceClassDefs = useVoiceClasses(uniqueVoiceIds);

    // Quick helper to fetch class for a voiceId
    const getVoiceClass = useCallback(
        (voiceId: string): string => {
            const hit = voiceClassDefs.find((d) => d.id === voiceId);
            return hit ? hit.cls : "";
        },
        [voiceClassDefs],
    );

    // Update segment voice
    const handleUpdateSegmentVoice = (segmentId: string, voiceId: string) => {
        // Track last selected voice for auto-fill
        setLastSelectedVoiceId(voiceId);
        // Mark as initialized since user explicitly selected a voice
        hasInitializedVoiceRef.current = true;

        const newSegments = segments.map((seg) => (seg.id === segmentId ? { ...seg, voiceId } : seg));
        onSegmentsChange(normalizeSegments(fullText, newSegments));
        setEditingVoiceId(null);
    };

    // Render text with highlights using stable keys and CSS classes
    const renderHighlightedText = () => {
        if (sortedSegments.length === 0) {
            return null;
        }

        const parts: Array<{ text: string; voiceId: string | null; start: number; end: number }> = [];
        let cursor = 0;

        sortedSegments.forEach((segment) => {
            // Add text before segment
            if (segment.startIndex > cursor) {
                parts.push({
                    text: fullText.substring(cursor, segment.startIndex),
                    voiceId: null,
                    start: cursor,
                    end: segment.startIndex,
                });
            }

            // Add segment - use the actual text from fullText to ensure consistency
            parts.push({
                text: fullText.substring(segment.startIndex, segment.endIndex),
                voiceId: segment.voiceId,
                start: segment.startIndex,
                end: segment.endIndex,
            });

            cursor = segment.endIndex;
        });

        // Add remaining text
        if (cursor < fullText.length) {
            parts.push({
                text: fullText.substring(cursor),
                voiceId: null,
                start: cursor,
                end: fullText.length,
            });
        }

        return (
            <div className={styles["overlay-inner"]}>
                {parts.map((p) => {
                    if (!p.voiceId) {
                        return <span key={`n-${p.start}-${p.end}`}>{p.text || "\u00A0"}</span>;
                    }
                    const voiceClass = getVoiceClass(p.voiceId);
                    const voiceDef = voiceClassDefs.find(
                        (v: { id: string; cls: string; bg: string; avatar: string }) => v.id === p.voiceId,
                    );
                    const voiceColor = voiceDef?.bg || VOICE_COLORS[0];
                    // Use inline style as fallback if class isn't available
                    const style = voiceClass ? {} : { backgroundColor: voiceColor, textDecoration: "underline" };
                    return (
                        <span
                            key={`v-${p.voiceId}-${p.start}-${p.end}`}
                            className={
                                voiceClass ? `${styles["highlighted-text"]} ${voiceClass}` : styles["highlighted-text"]
                            }
                            style={style}
                        >
                            {p.text || "\u00A0"}
                        </span>
                    );
                })}
            </div>
        );
    };

    // Generate a consistent avatar color for each voice (saturated, for speaker pills)
    const getVoiceAvatarColor = (voiceId: string): string => {
        const voiceDef = voiceClassDefs.find(
            (v: { id: string; cls: string; bg: string; avatar: string }) => v.id === voiceId,
        );
        return voiceDef?.avatar || VOICE_AVATAR_COLORS[0];
    };

    // Get first letter from voice name (uppercase)
    const getVoiceInitial = (voiceId: string): string => {
        const voiceInfo = voiceOptions.find((v) => v.value === voiceId);
        const voiceName = voiceInfo?.name || voiceInfo?.label || "";
        if (!voiceName) return "?";

        // Get first letter, uppercase
        return voiceName.trim().charAt(0).toUpperCase();
    };

    // Get unique voices from segments - badges always at top
    const uniqueVoices = useMemo(() => {
        const voiceMap = new Map<string, TextSegment>();
        sortedSegments.forEach((segment) => {
            if (segment.voiceId && !voiceMap.has(segment.voiceId)) {
                voiceMap.set(segment.voiceId, segment);
            }
        });
        return Array.from(voiceMap.values());
    }, [sortedSegments]);

    // Calculate required badge container height based on badge positions (single column only)
    const calculateBadgeContainerHeight = useCallback(() => {
        if (uniqueVoices.length === 0) {
            return Math.max(textareaHeight, 100); // At least match textarea or min height
        }

        // Speaker pills: ~28px tall, 8px gap (mockup .spk)
        const spacing = 36;
        const badgeHeight = 28;
        const lastIndex = uniqueVoices.length - 1;
        const maxBottom = 10 + lastIndex * spacing + badgeHeight;

        // Add bottom padding
        const requiredHeight = maxBottom + 10;

        // Ensure it's at least as tall as the textarea
        return Math.max(requiredHeight, Math.max(textareaHeight, 100));
    }, [uniqueVoices.length, textareaHeight]);

    // Calculate badge container height
    const badgeContainerHeight = calculateBadgeContainerHeight();

    // Auto-resize textarea based on content and sync with badge container height
    useEffect(() => {
        const textarea = textareaRef.current;
        const wrapper = wrapperRef.current;
        if (textarea) {
            // Temporarily remove height constraint to get true scrollHeight
            const originalMaxHeight = textarea.style.maxHeight;
            textarea.style.height = "auto";
            textarea.style.maxHeight = "none";

            // Get the true scrollHeight without constraints
            const contentHeight = textarea.scrollHeight;

            // Calculate badge container required height
            const badgeHeight = calculateBadgeContainerHeight();

            // Use the maximum of content height and badge height
            const targetHeight = Math.max(contentHeight, badgeHeight);

            // Restore original constraints and set new height
            textarea.style.height = `${targetHeight}px`;
            textarea.style.maxHeight = originalMaxHeight || "";
            setTextareaHeight(targetHeight);

            // Sync wrapper height to match target height
            if (wrapper) {
                wrapper.style.height = `${targetHeight}px`;
            }
        }
    }, [fullText, calculateBadgeContainerHeight]);

    // Also resize on mount
    useEffect(() => {
        const textarea = textareaRef.current;
        const wrapper = wrapperRef.current;
        if (textarea) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                const originalMaxHeight = textarea.style.maxHeight;
                textarea.style.height = "auto";
                textarea.style.maxHeight = "none";
                const contentHeight = textarea.scrollHeight;

                // Calculate badge container required height
                const badgeHeight = calculateBadgeContainerHeight();

                // Use the maximum of content height and badge height
                const targetHeight = Math.max(contentHeight, badgeHeight);

                textarea.style.height = `${targetHeight}px`;
                textarea.style.maxHeight = originalMaxHeight || "";
                setTextareaHeight(targetHeight);

                // Sync wrapper height to match target height
                if (wrapper) {
                    wrapper.style.height = `${targetHeight}px`;
                }
            });
        }
    }, [calculateBadgeContainerHeight]);

    // Recalculate height when badges change (voices added/removed or column layout changes)
    useEffect(() => {
        const textarea = textareaRef.current;
        const wrapper = wrapperRef.current;

        if (textarea) {
            const originalMaxHeight = textarea.style.maxHeight;
            textarea.style.height = "auto";
            textarea.style.maxHeight = "none";
            const contentHeight = textarea.scrollHeight;

            // Calculate badge container required height
            const badgeHeight = calculateBadgeContainerHeight();

            // Use the maximum of content height and badge height
            const targetHeight = Math.max(contentHeight, badgeHeight);

            textarea.style.height = `${targetHeight}px`;
            textarea.style.maxHeight = originalMaxHeight || "";
            setTextareaHeight(targetHeight);

            // Sync wrapper height to match target height
            if (wrapper) {
                wrapper.style.height = `${targetHeight}px`;
            }
        }
    }, [uniqueVoices.length, calculateBadgeContainerHeight]);

    // Fixed badge container width - always reserve space to prevent layout shifts
    const BADGE_CONTAINER_WIDTH = 48; // Speaker pill width (padding + avatar)

    return (
        <div
            ref={containerRef}
            className={styles["text-with-voice-selection-container"]}
            data-rw-control="text-with-voice"
        >
            <div style={{ display: "flex", width: "100%" }}>
                {/* Left sidebar with voice badges - always visible with fixed width */}
                <div
                    ref={badgeContainerRef}
                    className={styles.speakersCol}
                    style={{
                        width: `${BADGE_CONTAINER_WIDTH}px`,
                        minWidth: `${BADGE_CONTAINER_WIDTH}px`,
                        height: `${badgeContainerHeight}px`,
                        minHeight: `${badgeContainerHeight}px`,
                    }}
                >
                    {uniqueVoices.map((segment, index) => {
                        const voiceInfo = voiceOptions.find((v) => v.value === segment.voiceId);
                        const voiceName = voiceInfo?.name || voiceInfo?.label || t("voice");
                        const initial = getVoiceInitial(segment.voiceId);
                        const avatarColor = getVoiceAvatarColor(segment.voiceId);

                        const spacing = 36;
                        const topPosition = 10 + index * spacing;

                        return (
                            <Tooltip key={segment.voiceId} title={voiceName} placement="right" arrow>
                                <button
                                    type="button"
                                    className={styles.spk}
                                    style={{
                                        top: `${topPosition}px`,
                                    }}
                                    onClick={() =>
                                        setEditingVoiceId((current) =>
                                            current === segment.voiceId ? null : segment.voiceId,
                                        )
                                    }
                                    aria-label={voiceName}
                                    aria-expanded={editingVoiceId === segment.voiceId}
                                >
                                    <span className={styles.voiceAv} style={{ backgroundColor: avatarColor }}>
                                        {initial}
                                    </span>
                                </button>
                            </Tooltip>
                        );
                    })}
                </div>

                <div
                    className={styles["text-input-wrapper"]}
                    data-rw-part="input-wrap"
                    style={{ flex: 1, paddingLeft: "6px" }}
                >
                    <div ref={wrapperRef} className={styles["hi-wrapper"]}>
                        {/* Voice stylesheet - generates CSS classes once per voice */}
                        <VoiceStyles defs={voiceClassDefs} />
                        {/* HIGHLIGHT OVERLAY (under the textarea) */}
                        <div className={styles["text-highlight-overlay"]} aria-hidden="true">
                            {renderHighlightedText()}
                        </div>

                        {/* TEXTAREA (on top, background transparent) */}
                        <textarea
                            ref={textareaRef}
                            className={styles["text-with-voice-textarea"]}
                            data-rw-part="textarea"
                            value={fullText}
                            onChange={(e) => {
                                const nextText = e.target.value;
                                const prevText = fullText;

                                // 1) update text in parent first
                                onTextChange(nextText);

                                // 2) rebase segments against the actual edit
                                const nextSegs = rebaseSegmentsAfterEdit(prevText, nextText, segments);
                                onSegmentsChange(nextSegs);

                                // auto-resize - temporarily remove constraints to get true scrollHeight
                                const ta = e.currentTarget;
                                const wrapper = wrapperRef.current;
                                const originalMaxHeight = ta.style.maxHeight;
                                ta.style.height = "auto";
                                ta.style.maxHeight = "none";
                                const contentHeight = ta.scrollHeight;

                                // Calculate badge container required height
                                const badgeHeight = calculateBadgeContainerHeight();

                                // Use the maximum of content height and badge height
                                const targetHeight = Math.max(contentHeight, badgeHeight);

                                ta.style.height = `${targetHeight}px`;
                                ta.style.maxHeight = originalMaxHeight || "";
                                setTextareaHeight(targetHeight);

                                // Sync wrapper height to match target height
                                if (wrapper) {
                                    wrapper.style.height = `${targetHeight}px`;
                                }
                            }}
                            onInput={(e) => {
                                const ta = e.currentTarget;
                                const wrapper = wrapperRef.current;
                                const originalMaxHeight = ta.style.maxHeight;
                                ta.style.height = "auto";
                                ta.style.maxHeight = "none";
                                const contentHeight = ta.scrollHeight;

                                // Calculate badge container required height
                                const badgeHeight = calculateBadgeContainerHeight();

                                // Use the maximum of content height and badge height
                                const targetHeight = Math.max(contentHeight, badgeHeight);

                                ta.style.height = `${targetHeight}px`;
                                ta.style.maxHeight = originalMaxHeight || "";
                                setTextareaHeight(targetHeight);

                                // Sync wrapper height to match target height
                                if (wrapper) {
                                    wrapper.style.height = `${targetHeight}px`;
                                }
                            }}
                            onSelect={handleTextSelection}
                            onMouseUp={handleTextSelection}
                            onKeyUp={handleTextSelection}
                            placeholder={placeholder}
                        />
                    </div>
                </div>
            </div>

            {/* Dropdown for editing when a voice badge is clicked */}
            {editingVoiceId &&
                (() => {
                    // Find all segments with the editing voice
                    const segmentsWithSameVoice = sortedSegments.filter((s) => s.voiceId === editingVoiceId);

                    if (segmentsWithSameVoice.length === 0) return null;

                    return (
                        <div
                            style={{
                                padding: "8px 10px",
                                borderTop: "1px solid #e0e0e0",
                                backgroundColor: "#fafafa",
                            }}
                        >
                            <Dropdown
                                options={voiceOptions}
                                placeholder={t("selectAVoice")}
                                value={editingVoiceId || ""}
                                onChange={(voiceId: string) => {
                                    // Update all segments with the same voice, then close the selector
                                    segmentsWithSameVoice.forEach((seg) => {
                                        handleUpdateSegmentVoice(seg.id, voiceId);
                                    });
                                    setEditingVoiceId(null);
                                }}
                                loading={false}
                                error={false}
                                label=""
                                isWithoutMargin={true}
                            />
                        </div>
                    );
                })()}
        </div>
    );
};

export default TextWithVoiceSelection;
